import { LogOut, Menu, UserCog, Users, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { apps, SWITCHER_DESTINATIONS } from "../lib/apps";
import type { SwitcherDestination } from "../lib/apps";

export interface SwitcherUser {
  id: number;
  displayName: string;
  isAdmin: boolean;
}

interface AppSwitcherProps {
  currentDestination: SwitcherDestination;
  user?: SwitcherUser;
  onSignOut?: () => Promise<void> | void;
}

const HOME_ROOT_CLASS = "app-home";

function destinationClass(destination: SwitcherDestination): string {
  return destination === "home" ? HOME_ROOT_CLASS : `app-${destination}`;
}

export function AppSwitcher({ currentDestination, user, onSignOut }: AppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Focus the dialog itself first. Opening from an app used to put focus on
    // the inactive Home link, whose focus ring could be clipped on small
    // screens. Tab still enters the destination list in its natural order.
    panelRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        return;
      }
      if (event.key === "Tab" && panelRef.current) {
        const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>('[data-switcher-focusable="true"]')).filter(el => el.getAttribute("tabindex") !== "-1");
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    function onPointerDown(event: PointerEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node) && !buttonRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
      document.body.style.overflow = originalOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  async function handleSignOut() {
    if (signingOut || !onSignOut) return;
    setSigningOut(true);
    try {
      await onSignOut();
    } finally {
      setSigningOut(false);
      setOpen(false);
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="app-switcher-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="app-switcher-dialog"
        aria-label="Family apps menu"
        onClick={() => setOpen(current => !current)}
      >
        {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
      </button>

      <div className="app-switcher-layer" data-testid="app-switcher-layer" hidden={!open}>
        <button
          type="button"
          className="app-switcher-scrim"
          aria-label="Close family apps menu"
          onClick={() => setOpen(false)}
          tabIndex={-1}
        />
        <div
          ref={panelRef}
          id="app-switcher-dialog"
          className="app-switcher-panel"
          role="dialog"
          tabIndex={-1}
          aria-modal="true"
          aria-label="Family apps"
        >
          <nav aria-label="Family apps" className="app-switcher-nav">
            <ul className="app-switcher-list">
              {SWITCHER_DESTINATIONS.map(destination => {
                const app = apps[destination];
                const Icon = app.Icon;
                const isCurrent = destination === currentDestination;
                return (
                  <li key={destination}>
                    <a
                      href={app.launcherHref ?? app.href}
                      className={`app-switcher-link${isCurrent ? " is-current" : ""}`}
                      aria-current={isCurrent ? "page" : undefined}
                      data-switcher-link="true"
                      data-switcher-focusable="true"
                      tabIndex={isCurrent ? -1 : 0}
                      aria-label={isCurrent ? `${app.title} (current page)` : app.title}
                    >
                      <span className={`app-switcher-icon ${destinationClass(destination)}`} aria-hidden="true"><Icon size={22} strokeWidth={1.8} /></span>
                      <span className="app-switcher-copy">
                        <span className="app-switcher-name">{app.title}</span>
                        <span className="app-switcher-description">{app.description}</span>
                      </span>
                      {isCurrent && <span className="app-switcher-current" aria-hidden="true" />}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          {user && (
            <section className="app-switcher-account" aria-label="Account actions">
              <div className="app-switcher-account-row">
                <a
                  href="/settings/"
                  className="app-switcher-account-link app-switcher-profile-link"
                  data-switcher-focusable="true"
                  data-tooltip="Account settings"
                  aria-label={`Account settings for ${user.displayName}`}
                >
                  <span className="app-switcher-account-avatar" aria-hidden="true">{user.displayName.charAt(0).toUpperCase()}</span>
                  <span className="app-switcher-account-name">{user.displayName}</span>
                  <UserCog size={18} aria-hidden="true" />
                </a>
                {user.isAdmin && (
                  <a
                    href="/admin/"
                    className="app-switcher-account-link app-switcher-account-icon-link"
                    data-switcher-focusable="true"
                    data-tooltip="People"
                    aria-label="People (administrators only)"
                  >
                    <span className="app-switcher-account-icon" aria-hidden="true"><Users size={18} /></span>
                  </a>
                )}
                <button
                  type="button"
                  className="app-switcher-signout app-switcher-account-icon-link"
                  data-switcher-focusable="true"
                  data-tooltip={signingOut ? "Signing out…" : "Sign out"}
                  onClick={() => void handleSignOut()}
                  disabled={signingOut}
                >
                  <LogOut size={18} aria-hidden="true" />
                  <span className="sr-only">{signingOut ? "Signing out…" : "Sign out"}</span>
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
